import { useUserAssessmentHooks, useUserAssessments } from '@api/assessmentQuestionnaireApiHandler';
import { useBoostersAndDrainersHooks } from '@api/boostersAndDrainersApiHandler';
import { useGetAllExperts } from '@api/expertsApiHandler';
import { useFocusAreasHooks } from '@api/focusAreasApiHandler';
import {
  IndicatorHistoryResponse,
  QuestionnaireIndicator,
  useIndicatorsHooks,
} from '@api/indicatorsApiHandler';
import { fetchIndicatorLearningResources } from '@api/learningApiHandler';
import { useUserActionsHooks } from '@api/userActionsApiHandler';
import { IndicatorPrimarySourceTypes } from '@enums/IndicatorPrimarySourceTypes';
import { useAppDispatch } from '@hooks/useAppDispatch';
import { useAppSelector } from '@hooks/useAppSelector';
import { LoadingBackdrop } from '@shared/molecules';
import {
  ActionsSection,
  CurrentPrioritiesSection,
  ExpertsSection,
  InsightsSection,
  NextActionSection,
  RecommendedLearningsSection,
  ReflectionSection,
} from '@shared/organisms';
import { CoreLayout } from '@shared/templates';
import { useIsFetching, useQuery } from '@tanstack/react-query';
import { DEFAULT_HS_BLUE_COLOR } from 'custom.d';
import dayjs from 'dayjs';
import React, { useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router';
import { RootState } from 'store';
import {
  setChartSelectedAssessmentDetails,
  setChartSelectedIndicatorDetails,
  setLoadingScores,
} from 'store/Slices/AssessmentScoresSliceV2';
import { resetQuestionnaire } from 'store/Slices/QuestionnaireSliceV2';
import {
  CurrentPrioritiesSkeleton,
  WellbeingInsightsSkeleton,
  WellbeingPlanSkeleton,
} from './LoadingSkeletons';
import styles from './styles.module.scss';

export const AssessmentScoresPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const questionnaireId = params.get('questionnaireId');
  const validatedQuestionnaireId = questionnaireId ? Number(questionnaireId) : null;
  const indicatorId = params.get('indicatorId');
  const validatedIndicatorId = indicatorId ? Number(indicatorId) : null;
  const dispatch = useAppDispatch();
  const { loadingScores, chartSelectedAssessment } = useAppSelector(
    (state) => state.assessmentScores || {},
  );
  const companyConfigData = useAppSelector((state: RootState) => state.companyConfig.data);
  const { data: expertsData = { results: [], total: 0 }, isLoading: expertsLoading } =
    useGetAllExperts();
  const isUserActionsFetching = useIsFetching({ queryKey: ['user-actions'] }) > 0;

  useEffect(() => {
    dispatch(resetQuestionnaire());
  }, []);

  const {
    data: assessmentData,
    isLoading: assessmentsIsLoading,
    isError,
  } = useUserAssessments({
    questionnaireId: Number(questionnaireId),
    startDate: null,
    endDate: null,
  });

  const {
    data: activeFocusAreasResponse,
    isLoading: isLoadingActive,
    error: activeError,
    refetch: refetchActive,
  } = useFocusAreasHooks.useActiveFocusAreas();

  useEffect(() => {
    if (
      assessmentData?.success &&
      assessmentData?.responseObject &&
      assessmentData?.responseObject[0]
    ) {
      dispatch(setLoadingScores(true));
      requestAnimationFrame(() => {
        setTimeout(() => {
          dispatch(setLoadingScores(false));
        }, 100); // Much shorter delay
      });
      dispatch(
        setChartSelectedAssessmentDetails({
          assessmentId:
            assessmentData?.responseObject[0]?.questionnairesV2UserResponses[0]?.userAssessmentId,
          date:
            assessmentData?.responseObject[0]?.questionnairesV2UserResponses[0]?.createdAt || '',
          index: 0,
          score: assessmentData?.responseObject[0]?.totalScore || 0,
          questionnaireId: assessmentData?.responseObject[0]?.questionnaireId || 0,
          questionnaireName: assessmentData?.responseObject[0]?.questionnaireV2?.name || '',
        }),
      );
    } else if (!assessmentData && validatedIndicatorId) {
      setTimeout(() => {
        dispatch(setLoadingScores(false));
      }, 1000);
    }
  }, [assessmentData?.responseObject]);

  const {
    data: boostersAndDrainersDataByAssessment,
    isLoading: loadingBoostersAndDrainersByAssessment,
    isError: errorBoostersAndDrainersByAssessment,
  } = useBoostersAndDrainersHooks.useBoostersAndDrainersByAssessmentId(
    chartSelectedAssessment?.assessmentId || 0,
  );

  const {
    data: indicatorData,
    isLoading: indicatorDataLoading,
    error: indicatorDataError,
  } = useIndicatorsHooks.useIndicatorHistoryDetails(validatedIndicatorId);

  const {
    data: indicatorAssessmentData,
    isLoading: indicatorAssessmentDataLoading,
    error: indicatorAssessmentDataError,
  } = useIndicatorsHooks.useIndicatorAssessmentDetails(validatedIndicatorId);

  const {
    data: indicatorInsightData,
    isLoading: indicatorInsightDataLoading,
    error: indicatorInsightDataError,
  } = useIndicatorsHooks.useIndicatorKeyInsight(validatedIndicatorId);

  const {
    data: questionnaireInsightData,
    isLoading: questionnaireInsightDataLoading,
    error: questionnaireInsightDataError,
  } = useUserAssessmentHooks.useGetQuestionnaireKeyInsight(validatedQuestionnaireId);

  const {
    data: questionnaireActionsData,
    isLoading: questionnaireActionsDataLoading,
    error: questionnaireActionsDataError,
    refetch: refetchQuestionnaireActions,
  } = useUserActionsHooks.useGetQuestionnaireActions(validatedQuestionnaireId);

  const {
    data: insightsActionsData,
    isLoading: insightsActionsDataLoading,
    error: insightsActionsDataError,
    refetch: refetchInsightActions,
  } = useUserActionsHooks.useGetInsightActions(validatedIndicatorId);

  const {
    data: learningResources,
    isLoading: learningResourcesLoading,
    error: learningResourcesErrors,
  } = useQuery({
    queryKey: ['fetchIndicatorLearningResources', validatedIndicatorId || 1],
    queryFn: () => fetchIndicatorLearningResources(validatedIndicatorId || 1),
    enabled: true,
  });

  useEffect(() => {
    if (
      indicatorData?.success &&
      indicatorData?.responseObject &&
      indicatorData?.responseObject?.history[0] &&
      validatedIndicatorId
    ) {
      dispatch(setLoadingScores(true));
      requestAnimationFrame(() => {
        setTimeout(() => {
          dispatch(setLoadingScores(false));
        }, 100);
      });
      dispatch(
        setChartSelectedIndicatorDetails({
          id: indicatorData?.responseObject?.history[0]?.id,
          indicatorId: indicatorData?.responseObject?.history[0]?.indicatorId,
          score: indicatorData?.responseObject?.history[0]?.score,
          scoreSourceText: indicatorData?.responseObject?.history[0]?.scoreSourceText,
          indicatorName: indicatorData?.responseObject?.history[0]?.indicator?.name,
          status: indicatorData?.responseObject?.history[0]?.status,
          isPositive: indicatorData?.responseObject?.history[0]?.indicator?.isPositive,
        }),
      );
    } else if (!indicatorData && validatedQuestionnaireId) {
      setTimeout(() => {
        dispatch(setLoadingScores(false));
      }, 1000);
    }
  }, [indicatorData?.responseObject]);

  const userActions = useMemo(() => {
    if (validatedIndicatorId) {
      if (!insightsActionsData?.success) return [];

      return insightsActionsData.responseObject.filter((act) => !act?.completed).slice(0, 3) || [];
    }

    if (validatedQuestionnaireId) {
      if (!questionnaireActionsData?.success) return [];

      return (
        questionnaireActionsData.responseObject.filter((act) => !act?.completed).slice(0, 3) || []
      );
    }

    return [];
  }, [
    questionnaireActionsData,
    insightsActionsData,
    validatedIndicatorId,
    validatedQuestionnaireId,
  ]);

  const userAssessmentData = useMemo(() => {
    if (assessmentData?.success) {
      return assessmentData?.responseObject || [];
    }

    return [];
  }, [assessmentData]);

  const boostersAndDrainers = useMemo(() => {
    if (boostersAndDrainersDataByAssessment?.success) {
      return boostersAndDrainersDataByAssessment?.responseObject || null;
    }

    return null;
  }, [boostersAndDrainersDataByAssessment]);

  const wellbeingIndicator: IndicatorHistoryResponse | null = useMemo(() => {
    if (indicatorData?.success) {
      return indicatorData?.responseObject || null;
    }

    return null;
  }, [indicatorData, validatedIndicatorId]);

  const wellbeingIndicatorAssessment: QuestionnaireIndicator | null = useMemo(() => {
    if (indicatorAssessmentData?.success) {
      return indicatorAssessmentData?.responseObject || null;
    }

    return null;
  }, [indicatorAssessmentData, validatedIndicatorId]);

  const insightTexts: string[] | [] = useMemo(() => {
    if (validatedIndicatorId && indicatorInsightData?.success) {
      return indicatorInsightData?.responseObject?.insight || [];
    }

    if (validatedQuestionnaireId && questionnaireInsightData?.success) {
      return questionnaireInsightData?.responseObject?.insight || [];
    }

    return [];
  }, [
    indicatorInsightData,
    validatedIndicatorId,
    questionnaireInsightData,
    validatedQuestionnaireId,
  ]);

  const recommendedNextAiActions: string[] | [] = useMemo(() => {
    if (validatedIndicatorId && indicatorInsightData?.success) {
      return indicatorInsightData?.responseObject?.recommendedActions || [];
    }

    if (validatedQuestionnaireId && questionnaireInsightData?.success) {
      return questionnaireInsightData?.responseObject?.recommendedActions || [];
    }

    return [];
  }, [
    indicatorInsightData,
    validatedIndicatorId,
    questionnaireInsightData,
    validatedQuestionnaireId,
  ]);

  const learnings: { id: number; innerImageUrl: string; title: string }[] | [] = useMemo(() => {
    if (learningResources?.success) {
      return (
        learningResources?.responseObject.map((dt) => ({
          id: dt.id,
          innerImageUrl: dt.imageUrl,
          title: dt.title,
        })) || []
      );
    }

    return [];
  }, [learningResources, validatedIndicatorId]);

  const insightsLoading =
    assessmentsIsLoading ||
    indicatorDataLoading ||
    indicatorAssessmentDataLoading ||
    indicatorInsightDataLoading ||
    questionnaireInsightDataLoading;
  const currentPrioritiesLoading = isLoadingActive;
  const actionsLoading = insightsActionsDataLoading || questionnaireActionsDataLoading;
  const overallLoading =
    indicatorInsightDataLoading ||
    loadingScores ||
    assessmentsIsLoading ||
    indicatorDataLoading ||
    indicatorAssessmentDataLoading ||
    loadingBoostersAndDrainersByAssessment ||
    (!validatedIndicatorId && !validatedQuestionnaireId);

  const FOUR_WEEKS_IN_DAYS = 28;

  const indicatorSpecializedAssessmentData = useMemo(() => {
    if (!validatedIndicatorId || !wellbeingIndicator) {
      return {
        isOutdated: false,
        indicatorData: null,
      };
    }

    const userTakenSpecializedAssessment =
      wellbeingIndicator?.history?.find(
        (wd) => wd.primarySourceType === IndicatorPrimarySourceTypes.SPECIALISED_QUESTIONNAIRE,
      ) || null;

    if (!userTakenSpecializedAssessment) {
      return {
        isOutdated: false,
        indicatorData: null,
      };
    }

    if (!userTakenSpecializedAssessment?.createdAt) {
      return {
        isOutdated: false,
        indicatorData: userTakenSpecializedAssessment,
      };
    }

    const createdAt = dayjs(userTakenSpecializedAssessment.createdAt);
    const isOutdated = dayjs().diff(createdAt, 'day') > FOUR_WEEKS_IN_DAYS;

    return {
      isOutdated,
      indicatorData: userTakenSpecializedAssessment,
    };
  }, [wellbeingIndicator?.history, validatedIndicatorId]);

  return (
    <CoreLayout title="Assessment Scores" navigationType="default" fullWidthEnabled>
      {overallLoading && (
        <LoadingBackdrop
          text={
            validatedQuestionnaireId || validatedIndicatorId || indicatorDataError
              ? 'Analysing your responses — almost ready…'
              : `Something didn’t go as planned — your assessment data couldn’t be located.`
          }
          goBackEnabled
        />
      )}

      <div
        style={{
          backgroundColor: companyConfigData.dashboardBgColour
            ? companyConfigData.dashboardBgColour
            : DEFAULT_HS_BLUE_COLOR,
        }}
        className={styles.assessmentScoresContainer}
      >
        <div className={styles.sections}>
          <div className="container">
            {insightsLoading ? (
              <WellbeingInsightsSkeleton />
            ) : (
              <InsightsSection
                assessmentData={userAssessmentData}
                boostersAndDrainers={boostersAndDrainers}
                isIndicatorEnabled={validatedIndicatorId ? true : (false as unknown as false)}
                wellbeingIndicatorDetails={
                  validatedIndicatorId && wellbeingIndicator ? wellbeingIndicator : null
                }
                wellbeingIndicatorAssessment={wellbeingIndicatorAssessment}
                insightTexts={insightTexts}
                userTakenSpecializedAssessmentInIndicator={
                  indicatorSpecializedAssessmentData?.indicatorData
                }
              />
            )}

            {currentPrioritiesLoading ? (
              <CurrentPrioritiesSkeleton />
            ) : (
              <CurrentPrioritiesSection
                activeError={activeError}
                isLoadingActive={isLoadingActive}
                selectedPriorities={
                  activeFocusAreasResponse?.success ? activeFocusAreasResponse?.responseObject : []
                }
                refetchActivePriorities={refetchActive}
                isIndicatorEnabled={validatedIndicatorId ? true : (false as unknown as false)}
              />
            )}

            {/* ******** user hasn't taken the related specialised assessment yet ******** */}
            {validatedIndicatorId &&
              wellbeingIndicatorAssessment?.questionnaire &&
              !indicatorSpecializedAssessmentData?.indicatorData && (
                <NextActionSection
                  nextActionQuestionnaireId={wellbeingIndicatorAssessment?.questionnaireId || null}
                  nextActionQuestionnaire={
                    wellbeingIndicatorAssessment?.questionnaire?.name || null
                  }
                  topText={`Take ${
                    wellbeingIndicatorAssessment?.questionnaire?.name || ''
                  } Assessment`}
                  middleText={`Take a specific ${
                    wellbeingIndicatorAssessment?.questionnaire?.name?.toLowerCase() || ''
                  } assessment to explore your levels in detail`}
                  indicatorId={wellbeingIndicatorAssessment?.indicatorId || null}
                />
              )}

            {/* ******** user has already taken the related specialised assessment more than 4 weeks ago ******** */}
            {validatedIndicatorId &&
              wellbeingIndicatorAssessment?.questionnaire &&
              indicatorSpecializedAssessmentData?.indicatorData &&
              indicatorSpecializedAssessmentData?.isOutdated && (
                <NextActionSection
                  nextActionQuestionnaireId={wellbeingIndicatorAssessment?.questionnaireId || null}
                  nextActionQuestionnaire={
                    wellbeingIndicatorAssessment?.questionnaire?.name || null
                  }
                  topText={`Take ${
                    wellbeingIndicatorAssessment?.questionnaire?.name || ''
                  } Assessment`}
                  middleText={`Take a specific ${
                    wellbeingIndicatorAssessment?.questionnaire?.name?.toLowerCase() || ''
                  } assessment to explore your levels in detail`}
                  indicatorId={wellbeingIndicatorAssessment?.indicatorId || null}
                />
              )}

            {actionsLoading || isUserActionsFetching ? (
              <WellbeingPlanSkeleton />
            ) : (
              <ActionsSection
                userActions={userActions}
                refetchActions={() => {
                  if (validatedIndicatorId) {
                    refetchInsightActions();
                  } else if (validatedQuestionnaireId) {
                    refetchQuestionnaireActions();
                  }
                }}
                recommendedNextActions={recommendedNextAiActions}
                recommendedNextActionsLoading={
                  indicatorInsightDataLoading || questionnaireInsightDataLoading
                }
              />
            )}

            <ReflectionSection />

            <RecommendedLearningsSection
              learningPosts={learnings}
              loadingPosts={learningResourcesLoading}
            />

            <div className={styles.expertsSection}>
              {companyConfigData &&
                companyConfigData?.expertsEnabled &&
                expertsData?.results?.length > 0 && (
                  <ExpertsSection
                    experts={expertsData}
                    expertsLoading={expertsLoading}
                    isNotDashboard
                  />
                )}
            </div>
          </div>
        </div>
      </div>
    </CoreLayout>
  );
};
